/**
 * threeConfigurator.js - Main Orchestrator
 *
 * This is the main entry point that imports and coordinates all configurator modules.
 * It maintains the same public API as the original monolithic file.
 */

import * as THREE from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

// Import all modules
import * as state from './threeConfigurator/state.js';
import * as utils from './threeConfigurator/utils.js';
import * as sceneManager from './threeConfigurator/sceneManager.js';
import * as modelManager from './threeConfigurator/modelManager.js';
import * as materialManager from './threeConfigurator/materialManager.js';
import * as decalManager from './threeConfigurator/decalManager.js';
import * as graphicsManager from './threeConfigurator/graphicsManager.js';
import * as markerManager from './threeConfigurator/markerManager.js';
import * as interactionManager from './threeConfigurator/interactionManager.js';
import * as configurationManager from './threeConfigurator/configurationManager.js';
import {captureAiVisionHandover} from './threeConfigurator/aiVisionCapture.js';
import {initAiVisionStudioButton} from './threeConfigurator/aiVisionButton.js';
import {initAiVisionExport} from './threeConfigurator/aiVisionExport.js';

/**
 * Main function to create and initialize the Three.js configurator scene
 * @param {HTMLElement|string} container - DOM element or selector for the viewer container
 * @param {Object} options - Configuration options
 * @param {string} options.modelUrl - URL to the 3D model file
 * @param {string} options.hdrUrl - URL to the HDR environment map
 * @returns {Object} Public API with scene, camera, renderer, controls
 */
export function createThreeJSScene(container, options = {}) {

    // ===== SETUP CONSTANTS =====
    const defaultModel = '/userdata/files/models/premium-tourer.glb';
    const defaultHdr = '/userdata/files/models/provence_studio_1k.hdr';
    const GLOBALS = utils.getGlobals();

    // Check if there's a configuration UUID in the URL
    // If yes, we'll load the model AFTER loading the configuration to get the correct size
    const urlParams = new URLSearchParams(window.location.search);
    const hasConfigurationInUrl = urlParams.has('uuid');

    // Check for initial model selection from panel
    const panelContainer = document.querySelector('.configuratorPanel');
    const firstSizeInput = panelContainer?.querySelector('input[name="modelSize"]:checked')
        || panelContainer?.querySelector('input[name="modelSize"]');
    if (firstSizeInput && !hasConfigurationInUrl) {
        // Only use the panel selection if there's NO configuration in URL
        options.modelUrl = firstSizeInput.value;
    }

    // CMS-Setting (pro Modell) lesen: Sollen Decals auf Glas voll deckend (opak) sein?
    // Wert kommt vom data-glass-decals-opaque des initial gewählten Modell-Radios.
    // Bei UUID-Konfigurationen wird der korrekte Wert später via setModelRadioChecked gesetzt.
    modelManager.applyGlassDecalsOpaqueFromRadio(firstSizeInput);

    // Scoped navigation guard: within sizeOptions, prevent accidental anchor navigation
    // This keeps behavior limited to the configurator and avoids touching rest of the site
    const sizeOptions = panelContainer?.querySelector('.sizeOptions');
    if (sizeOptions && !sizeOptions.__navGuardAttached) {
        sizeOptions.__navGuardAttached = true;
        sizeOptions.addEventListener('click', (e) => {
            const a = e.target.closest('a');
            if (!a) return;
            // Allow javascript:void(0) and in-page anchors, block others in sizeOptions
            const href = a.getAttribute('href') || '';
            if (href && href !== '#' && !href.startsWith('javascript:')) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, { capture: true });
    }

    const modelUrl = options.modelUrl || defaultModel;
    const hdrUrl = options.hdrUrl || defaultHdr;

    // ===== SETUP CONTAINER =====
    let containerEl =
        typeof container === 'string'
            ? document.querySelector(container) || document.getElementById(container)
            : container instanceof Element
                ? container
                : document.getElementById('viewer');

    if (!containerEl) {
        console.error('[Configurator] Container element not found');
        return;
    }

    // Store containerEl in state for access by other modules
    state.setContainerEl(containerEl);

    // ===== SETUP LOADING MANAGER =====
    const loaderEl = document.getElementById('loader');
    const manager = new THREE.LoadingManager();

    // CRITICAL: DRACOLoader should NOT be passed the LoadingManager in constructor
    // This can cause issues in Firefox with WASM loading
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/layout/frontend/eila/dist/draco/');

    // Preload the decoder to avoid lazy-loading issues
    dracoLoader.preload();

    // Spinner wird im Template angezeigt, keine Progress-Updates mehr nötig
    manager.onProgress = (url, loaded, total) => {
        // Progress tracking entfernt - Spinner läuft automatisch
    };

    manager.onLoad = () => {
        utils.hideLoaderSmooth(loaderEl, containerEl);
    };

    // ===== INITIALIZATION SEQUENCE =====

    // 1. Initialize Scene, Camera, Renderer, Controls
    sceneManager.initScene(containerEl, hdrUrl, manager);

    // 2. Setup Environment (floor, grid, lights)
    sceneManager.setupEnvironment();

    // 3. Store Initial Camera View
    sceneManager.storeInitialView();

    // 4. Setup Zoom Controls
    const viewControlContainer = document.querySelector('.viewControl');
    const zoomValueEl = viewControlContainer?.querySelector('.zoomValue');
    if (zoomValueEl) {
        sceneManager.setZoomValueEl(zoomValueEl);
    }
    sceneManager.updateZoomDisplay();

    // Setup zoom button event listeners
    const zoomInBtn = viewControlContainer?.querySelector('.zoomIn');
    const zoomOutBtn = viewControlContainer?.querySelector('.zoomOut');
    const restViewBtn = viewControlContainer?.querySelector('.restViewButton');

    if (zoomInBtn) {
        zoomInBtn.addEventListener('click', () => {
            sceneManager.zoomIn();
        });
    }

    if (zoomOutBtn) {
        zoomOutBtn.addEventListener('click', () => {
            sceneManager.zoomOut();
        });
    }

    if (restViewBtn) {
        restViewBtn.addEventListener('click', () => {
            sceneManager.resetView();
        });
    }

    // Setup reset configuration button
    const resetConfigBtn = document.querySelector('.resetConfiguration');
    if (resetConfigBtn) {
        resetConfigBtn.addEventListener('click', () => {
            resetConfiguration();
        });
    }

    // 5. Setup Window Resize Handler
    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            sceneManager.onWindowResize(containerEl);
        }, 150);
        state.setNeedsMarkerUpdate(true);
    });

    // 6. Setup Model Reload Function
    function reloadModel(newModelUrl) {
        if (state.isLoading) return;

        modelManager.reloadModel(
            newModelUrl,
            manager,
            dracoLoader,
            loaderEl,
            containerEl,
            onModelReloaded
        );
    }

    // Expose reloadModel globally for configuration manager
    window._reloadModel = reloadModel;

    // Expose function to load default model (fallback for failed config loads)
    window._loadDefaultModel = () => {
        const panelContainer = document.querySelector('.configuratorPanel');
        const defaultSizeInput = panelContainer?.querySelector('input[name="modelSize"]:checked')
            || panelContainer?.querySelector('input[name="modelSize"]');
        const fallbackUrl = defaultSizeInput?.value || modelUrl;
        reloadModel(fallbackUrl);
    };

    // 6a. Setup Configuration Reset Function
    function resetConfiguration() {
        // Get current model URL from checked radio button
        const panelContainer = document.querySelector('.configuratorPanel');
        const selectedSizeInput = panelContainer?.querySelector('input[name="modelSize"]:checked');
        const currentModelUrl = selectedSizeInput?.value || modelUrl;

        // Reload the model - this will clear everything and rebuild the UI
        reloadModel(currentModelUrl);
    }

    // 7. Model Load Callbacks
    function onModelLoad(gltf) {
        modelManager.onModelLoad(
            gltf,
            sceneManager.updateGrid,
            buildUIFromMarkers,
            modelManager.rebuildOccludeTargets,
            markerManager.updateMarkers
        );
    }

    function onModelReloaded(gltf) {
        modelManager.onModelReloaded(
            gltf,
            () => modelManager.initSizeSelector(window._reloadModel),
            markerManager.updateMarkers,
            buildUIFromMarkers,
            modelManager.rebuildOccludeTargets
        );
    }

    // 8. Load Initial Model
    // Skip if there's a configuration in URL - it will load the correct model
    if (!hasConfigurationInUrl) {
        // Mark that we're loading the initial model to prevent double-loading
        window._initialModelLoaded = true;

        modelManager.loadModel(
            modelUrl,
            manager,
            dracoLoader,
            onModelLoad,
            (error) => {
                console.error('[Configurator] Model load error:', error);
                if (loaderEl) loaderEl.style.display = 'none';
            }
        );
    }

    // 9. Initialize Size Selector
    // Skip initial model load since we already loaded it above (or will load via config)
    // This just sets up the event listeners for future model changes
    modelManager.initSizeSelector(reloadModel, true); // Always skip initial load here

    // 10. Initialize CfgSlideItems
    utils.initCfgSlideItems('.cfgSlideItem', panelContainer);

    // 11. Start Animation Loop
    sceneManager.animate(decalManager.updateDecalOverlays);

    // 12. Setup Interaction Handler
    const canvas = state.renderer?.domElement;
    if (canvas) {
        function getMouse(e) {
            const rect = canvas.getBoundingClientRect();
            const mouse = new THREE.Vector2();
            mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            return mouse;
        }

        function setActiveSide(side) {
            state.setActiveSide(side);
        }

        function openPanel() {
            if (window._closeMobilePanel) {
                // Mobile panel is handled elsewhere
            }
        }

        canvas.addEventListener('pointerdown', (e) => {
            interactionManager.onPointerDown(e, getMouse, setActiveSide, openPanel);
        });

        canvas.addEventListener('pointermove', (e) => {
            interactionManager.onPointerMove(e, getMouse);
        });

        canvas.addEventListener('pointerup', (e) => {
            interactionManager.onPointerUp();
        });

        canvas.addEventListener('pointerleave', (e) => {
            interactionManager.onPointerUp();
        });

        canvas.addEventListener('pointercancel', (e) => {
            interactionManager.onPointerUp();
        });
    }

    // 13. Initialize Configurator Buttons (Save/Load/Share)
    configurationManager.initConfiguratorButtons();
    initAiVisionStudioButton();
    initAiVisionExport();

    // 13a. Initialize Configurator Headline
    configurationManager.initConfiguratorHeadline();

    // 14. Initialize Mobile Panel
    initMobilePanel();

    // 15. Check and Load Configuration from URL (with delay)
    setTimeout(() => {
        configurationManager.checkAndLoadConfigFromUrl();
    }, 500);

    // ===== HELPER FUNCTIONS =====

    /**
     * Build UI from model markers
     * NOTE: This is a placeholder - the full buildUIFromMarkers function
     * needs to be extracted from the backup file and added to markerManager.js
     */
    function buildUIFromMarkers(groups = window._configGroups) {
        markerManager.buildUIFromMarkers(groups);
    }

    /**
     * Initialize Mobile Panel Controls
     */
    function initMobilePanel() {
        const mobileButton = document.querySelector('.configuratorMobileButton');
        const panel = document.querySelector('.configuratorPanel');
        const overlay = document.querySelector('.configuratorOverlay');
        const closeBtn = panel?.querySelector('.panelCloseBtn');

        if (!mobileButton || !panel || !overlay) return;

        function openPanel() {
            panel.classList.add('panel-open');
            overlay.classList.add('overlay-active');
            document.body.style.overflow = 'hidden';
        }

        function closePanel() {
            panel.classList.remove('panel-open');
            overlay.classList.remove('overlay-active');
            document.body.style.overflow = '';
        }

        mobileButton.addEventListener('click', openPanel);

        if (closeBtn) {
            closeBtn.addEventListener('click', closePanel);
        }

        overlay.addEventListener('click', closePanel);

        window._closeMobilePanel = closePanel;
    }

    // ===== GLOBAL API EXPOSURE =====

    // Expose render request function globally
    window._threeRequestRender = sceneManager.requestRender;

    // Expose configuration functions globally
    window._saveConfiguration = configurationManager.saveConfiguration;
    window._loadConfiguration = configurationManager.loadConfiguration;
    window._applyConfigurationState = configurationManager.applyConfigurationState;
    window._collectConfigurationState = configurationManager.collectConfigurationState;

    // AI Vision Studio handover: high-res views and masks of the current configuration (#233679)
    window._captureAiVisionHandover = captureAiVisionHandover;

    // Expose debug functions
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        window._debugState = state;
        window._debugUtils = utils;
        window._debugScene = sceneManager;
        window._debugModel = modelManager;
        window._debugMaterial = materialManager;
        window._debugDecal = decalManager;
        window._debugGraphics = graphicsManager;
        window._debugMarker = markerManager;
        window._debugInteraction = interactionManager;
        window._debugConfiguration = configurationManager;
    }

    // ===== RETURN PUBLIC API =====
    return {
        scene: state.scene,
        camera: state.camera,
        renderer: state.renderer,
        controls: state.controls
    };
}
